import { Modal, UnstyledButton, TextInput, Button } from '@mantine/core';
import { HiPencil } from 'react-icons/hi2';
import { useState } from 'react';
import useAbsences from '@/hooks/employee/useAbsences';
import useDeleteApprovedAbsence from '@/hooks/employee/useDeleteApprovedAbsence';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { type Absence } from '@/schemas/API/GET/employee/absence/response';
import dayjs from 'dayjs';
import useEmployeeSchedule from '@/hooks/employee/useEmployeeSchedule';

export const ApprovedTimeOffCard: React.FC = () => {
  const { absences, error } = useAbsences();
  const { deleteApprovedAbsence } = useDeleteApprovedAbsence();
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null);
  const { mutate } = useEmployeeSchedule();

  if (error) return <SectionErrorNotification />;
  if (!absences) return <SectionLoadingOverlay />;
  let timeOffDays = 0;
  const totalDays = 50;
  const daysBetween = (startD: string, endD: string) => {
    const startDate = new Date(startD);
    const endDate = new Date(endD);
    const differenceInMs = endDate.getTime() - startDate.getTime();
    const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);
    return Math.ceil(differenceInDays);
  };
  absences.content.forEach(absence => {
    if (absence.confirm) {
      timeOffDays += daysBetween(absence.start, absence.end);
    }
  });
  const closeModal = () => {
    setSelectedAbsence(null);
  };
  const openModal = (absenceData: Absence) => {
    setSelectedAbsence(absenceData);
  };
  const dateToShow = (date: string) => {
    const dateWithoutTime = date.split('T')[0];
    return dayjs(dateWithoutTime).format('DD.MM.YYYY');
  };
  async function handleDelete(id: number) {
    await deleteApprovedAbsence(id);
    closeModal();
    await mutate();
  }
  return (
    <>
      {selectedAbsence && (
        <Modal
          size="xl"
          opened={true}
          onClose={closeModal}
          centered
          title="Dovolená"
        >
          <div className="flex flex-row gap-3 mb-5">
            <div className="w-1/2 flex flex-col">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="note"
                label="Začátek"
                value={dateToShow(selectedAbsence.start)}
                readOnly
              />
            </div>
            <div className="w-1/2 flex flex-col justify-between">
              <TextInput
                variant="filled"
                radius="md"
                placeholder="note"
                label="Konec"
                value={dateToShow(selectedAbsence.end)}
                readOnly
              />
            </div>
          </div>
          <p className="text-sm mb-4 mt-3">
            Délka dovolené:{' '}
            {daysBetween(selectedAbsence.start, selectedAbsence.end)} dnů
          </p>
          <TextInput
            variant="filled"
            radius="md"
            placeholder="note"
            label="Poznámka"
            value={selectedAbsence.note}
            readOnly
          />
          <h2 className="text-lg font-semibold">Počet dní dovolené:</h2>
          <p className="text-sm mb-4 mt-3">
            Schválené dovolené: {timeOffDays} dnů (/{totalDays} maximum)
          </p>
          <Button
            variant="outline"
            radius="md"
            onClick={() => {
              handleDelete(selectedAbsence.id);
            }}
            className="w-full"
          >
            Odstranit
          </Button>
        </Modal>
      )}
      <div className="w-full box-border border border-card rounded-card p-2.5">
        <h2 className="text-lg font-semibold">Schválené dovolené</h2>
        {timeOffDays > 0 ? (
          <>
            <p className="text-sm mb-4 mt-3">
              Počet dní dovolené: {timeOffDays}/{totalDays}
            </p>
            <ul>
              {absences.content.map(a =>
                a.confirm ? (
                  <li
                    key={a.id}
                    className="flex justify-between items-center mb-2"
                  >
                    <span>
                      {dateToShow(a.start)} - {dateToShow(a.end)}
                    </span>
                    <UnstyledButton onClick={() => openModal(a)}>
                      <div className="flex items-center">
                        <HiPencil color="black" size="1.5em" />
                      </div>
                    </UnstyledButton>
                  </li>
                ) : null
              )}
            </ul>
          </>
        ) : (
          <h3 className="text-sm mb-4 mt-3">Nejsou žádné schválené dovolené</h3>
        )}
      </div>
    </>
  );
};
